import React, { useState, useEffect } from 'react';
import { Calendar, FileText, Filter } from 'lucide-react';
import { pricingService } from '../../services/pricingService';
import LoadingSpinner from '../LoadingSpinner';
import './PricingHistoryView.css';

// Only show changes to these fields (exclude formula fields)
const VISIBLE_FIELDS = ['base_amount', 'per_acre_rate', 'current_value'];

const PricingHistoryView = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTable, setFilterTable] = useState('all');

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);

      let historyData = [];
      try {
        historyData = await pricingService.getPricingHistory({});
      } catch (err) {
        console.error('Error fetching pricing history:', err);
      }

      // Filter to only show relevant field changes
      const filtered = (historyData || []).filter(h =>
        VISIBLE_FIELDS.includes(h.field_name)
      );

      setHistory(filtered);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFieldName = (fieldName) => {
    if (fieldName === 'base_amount') return 'Base Amount';
    if (fieldName === 'per_acre_rate') return 'Per Acre Rate';
    if (fieldName === 'current_value') return 'Value';
    return fieldName;
  };

  const formatTableName = (tableName) => {
    if (tableName === 'pricing_tiers') return 'Tier';
    if (tableName === 'pricing_adjustments') return 'Adjustment';
    return tableName;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return `$${num.toLocaleString()}`;
    }
    return value;
  };

  const getFilteredHistory = () => {
    if (filterTable === 'all') return history;
    return history.filter(h => h.table_name === filterTable);
  };

  if (loading) {
    return (
      <div className="history-view">
        <LoadingSpinner />
      </div>
    );
  }

  const filteredHistory = getFilteredHistory();

  return (
    <div className="history-view">
      {history.length > 0 && (
        <div className="history-header">
          <div className="history-filter">
            <Filter size={16} />
            <select
              value={filterTable}
              onChange={(e) => setFilterTable(e.target.value)}
            >
              <option value="all">All Changes</option>
              <option value="pricing_tiers">Tier Changes</option>
              <option value="pricing_adjustments">Adjustment Changes</option>
            </select>
          </div>
        </div>
      )}

      {filteredHistory.length === 0 ? (
        <div className="history-empty">
          <Calendar size={40} />
          <h3>No pricing history</h3>
          <p>Changes to pricing will appear here</p>
        </div>
      ) : (
        <div className="history-table">
          <div className="history-table-header">
            <div className="col-date">Date</div>
            <div className="col-type">Type</div>
            <div className="col-item">Field</div>
            <div className="col-change">Change</div>
            <div className="col-reason">Reason</div>
          </div>

          <div className="history-table-body">
            {filteredHistory.map((item, index) => (
              <div key={index} className="history-row">
                <div className="col-date">
                  {formatDate(item.changed_at)}
                </div>
                <div className="col-type">
                  <span className="type-badge change">
                    <FileText size={12} />
                    {formatTableName(item.table_name)}
                  </span>
                </div>
                <div className="col-item">
                  {formatFieldName(item.field_name)}
                </div>
                <div className="col-change">
                  <span className="change-from">{formatValue(item.old_value)}</span>
                  <span className="change-arrow">→</span>
                  <span className="change-to">{formatValue(item.new_value)}</span>
                </div>
                <div className="col-reason">
                  {item.change_reason || '-'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingHistoryView;
